import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "logiciel-plante-ne-souvre-pas",
  silo: "materiel",
  h1: "Un logiciel plante ou ne s'ouvre plus sur Windows : que faire ?",
  metaTitle: "Logiciel qui plante ou ne s'ouvre pas : solutions",
  metaDescription: "Un programme se bloque ou refuse de démarrer ? Suivez nos conseils pour réparer ou réinstaller un logiciel sans perdre vos données.",
  excerpt: "Votre logiciel préféré se ferme brutalement, refuse de s'ouvrir, ou se bloque sur l'écran de chargement ? Ce problème courant a plusieurs solutions, de la plus simple à la plus radicale.",
  publishedAt: "2024-02-15",
  readingMinutes: 5,
  relatedSlugs: ["publicites-popups-intempestifs", "erreurs-windows-frequentes", "mise-a-jour-windows-bloquee", "imprimante-non-detectee"],
  faq: [
    {
      question: "Puis-je réparer un logiciel sans le désinstaller ?",
      answer: "Souvent oui. De nombreux logiciels proposent une option de réparation : Paramètres > Applications > cliquez sur le logiciel > Modifier. Cette option réinstalle les fichiers manquants sans toucher à vos données.",
    },
    {
      question: "Mon logiciel fonctionnait avant une mise à jour Windows -- comment revenir en arrière ?",
      answer: "Dans Paramètres > Windows Update > Historique des mises à jour > Désinstaller les mises à jour, trouvez la mise à jour installée avant le problème et désinstallez-la. Ou contactez l'éditeur du logiciel qui a peut-être déjà publié un correctif.",
    },
    {
      question: "Le logiciel s'ouvre mais plante au bout de quelques minutes -- c'est quoi le problème ?",
      answer: "Plusieurs causes possibles : insuffisance de RAM, surchauffe du processeur, conflit avec un autre logiciel, ou fichiers du logiciel corrompus. Vérifiez la température du PC et l'utilisation mémoire dans le Gestionnaire des tâches pendant le plantage.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Quand un logiciel cesse de fonctionner, c'est frustrant -- surtout s'il contient des données importantes (comptabilité, photos, documents). Voici les étapes à suivre dans l'ordre.",
    },
    {
      type: "h2",
      text: "Étape 1 : redémarrer le PC",
    },
    {
      type: "p",
      text: "Simple mais efficace. Un redémarrage vide la mémoire, ferme les processus bloqués en arrière-plan et repart de zéro. Si le logiciel planté était le seul problème, ça suffira.",
    },
    {
      type: "h2",
      text: "Étape 2 : tuer le processus bloqué",
    },
    {
      type: "p",
      text: "Si le logiciel est bloqué et ne répond plus, ouvrez le Gestionnaire des tâches (Ctrl + Maj + Échap). Trouvez le logiciel dans la liste, faites un clic droit dessus et sélectionnez 'Fin de tâche'. Attendez 30 secondes avant de le relancer.",
    },
    {
      type: "h2",
      text: "Étape 3 : lancer le logiciel en tant qu'administrateur",
    },
    {
      type: "p",
      text: "Certains logiciels ont besoin de droits d'administrateur pour fonctionner correctement. Clic droit sur l'icône du logiciel > Exécuter en tant qu'administrateur. Si ça fonctionne, vous pouvez configurer cette option de façon permanente dans les propriétés du logiciel.",
    },
    {
      type: "h2",
      text: "Étape 4 : réparer le logiciel",
    },
    {
      type: "p",
      text: "Paramètres > Applications > cherchez le logiciel > cliquez dessus > Modifier (ou Réparer si l'option existe). Cette procédure vérifie l'intégrité des fichiers du logiciel et remplace ceux qui sont corrompus sans toucher à vos données.",
    },
    {
      type: "callout",
      variant: "info",
      title: "Vérifier la compatibilité",
      text: "Si le logiciel est ancien, il peut ne pas être compatible avec votre version de Windows 11. Clic droit sur l'exécutable > Propriétés > onglet Compatibilité. Essayez de l'exécuter en mode de compatibilité Windows 10 ou 7.",
    },
    {
      type: "h2",
      text: "Étape 5 : désinstaller et réinstaller proprement",
    },
    {
      type: "p",
      text: "Si rien ne fonctionne, désinstallez le logiciel via Paramètres > Applications, puis téléchargez une version fraîche depuis le site officiel de l'éditeur. Avant de désinstaller, exportez vos données si possible (sauvegarde des paramètres, export des fichiers).",
    },
    {
      type: "h2",
      text: "Le logiciel plante à cause d'un virus",
    },
    {
      type: "p",
      text: "Des [virus et malwares](slug:publicites-popups-intempestifs) peuvent interférer avec le bon fonctionnement des logiciels. Si plusieurs programmes plantent en même temps, c'est un signal d'alarme. Lancez un scan antivirus complet. Des [erreurs Windows fréquentes](slug:erreurs-windows-frequentes) en parallèle confirment cette hypothèse.",
    },
    {
      type: "h2",
      text: "Besoin d'aide pour un logiciel spécifique ?",
    },
    {
      type: "p",
      text: "Certains logiciels métier (comptabilité, CAO, messagerie professionnelle) ont des configurations complexes. Nos techniciens maîtrisent les logiciels les plus courants et peuvent intervenir sur site pour diagnostiquer et corriger le problème sans toucher à vos données.",
    },
  ],
};

export default article;
